import RectangleEventExtensions from 'components/Markup/RectangleEventExtensions'
import defaultTo from 'ramda/src/defaultTo'
import { DEFAULT_LABEL_DIMENSIONS, DEFAULT_LABEL_ZOOM } from 'helpers/globals'

export default class extends google.maps.OverlayView {
  constructor({ map, dimensions, location, text, fillColor, strokeColor, enableEditMode, draggable }) {
    super()
    this.map = map
    this.dimensions = dimensions
    this.location = new google.maps.LatLng(location)
    this.text = defaultTo('Untitled Label')(text)
    this.fillColor = defaultTo('#ffffff')(fillColor)
    this.strokeColor = defaultTo('#000000')(strokeColor)
    this.draggable = draggable
    this.enableEditMode = enableEditMode
    this.containerElement = null
    this.rectangleInitialized = false
    this.setMap(map)
  }

  setDraggable(draggable) {
    if (this.rectangleInitialized) {
      this.rectangle.setOptions({ draggable: draggable })
    } else {
      this.draggable = draggable
    }
  }

  setEditable(editable) {
    if (this.rectangleInitialized) {
      this.rectangle.setEditable(editable)
    } else {
      this.editable = editable
    }
  }

  getCenter() {
    return this.rectangle.getBounds().getCenter()
  }

  getDimensions() {
    let bounds = this.rectangle.getBounds()
    let proj = this.getProjection()
    let swPx = proj.fromLatLngToDivPixel(bounds.getSouthWest())
    let nePx = proj.fromLatLngToDivPixel(bounds.getNorthEast())
    return {
      width: Math.abs((nePx.x - swPx.x).toFixed()),
      height: Math.abs((nePx.y - swPx.y).toFixed())
    }
  }

  getCorners() {
    let bounds = this.rectangle.getBounds()
    let NE = bounds.getNorthEast()
    let SW = bounds.getSouthWest()
    return {
      ne: NE,
      sw: SW,
      nw: new google.maps.LatLng(NE.lat(), SW.lng()),
      se: new google.maps.LatLng(SW.lat(), NE.lng())
    }
  }

  initRectangle(bounds) {
    this.rectangle = new google.maps.Rectangle({
      bounds,
      map: this.map,
      strokeColor: this.fillColor,
      draggable: this.draggable,
      editable: this.editable,
      suppressUndo: true,
      strokeOpacity: 0,
      fillOpacity: 0,
      zIndex: 108
    })

    this.rectangleInitialized = true

    new RectangleEventExtensions(this.rectangle)

    google.maps.event.addListener(this.rectangle, 'dblclick', e => {
      e.stop()
      this.edit()
    })

    google.maps.event.addListener(this.rectangle, 'resized', e => {
      this.draw(!window.metaKeyDown)
      google.maps.event.trigger(this, 'dimensions_changed', this.dimensions)
      google.maps.event.trigger(this, 'location_changed', this.getCenter())
    })

    google.maps.event.addListener(this.rectangle, 'bounds_changed', e => {
      this.draw(!window.metaKeyDown)
    })

    google.maps.event.addListener(this.rectangle, 'contextmenu', e => {
      google.maps.event.trigger(this, 'contextmenu', e)
    })

    google.maps.event.addListener(this.rectangle, 'mousedown', e => {
      google.maps.event.trigger(this, 'mousedown', e)
    })

    google.maps.event.addListener(this.rectangle, 'dragend', e => {
      google.maps.event.trigger(this, 'location_changed', this.getCenter())
    })
  }

  edit() {
    this.spanElement.contentEditable = true
    this.spanElement.focus()
    document.execCommand('selectAll', false, null)
  }

  setOptions(options) {
    return Object.keys(options).forEach(option => {
      switch (option) {
        case 'fillColor':
          this.fillColor = options['fillColor']
          this.containerElement.style.backgroundColor = this.fillColor
          this.rectangle.setOptions({ strokeColor: this.fillColor })
          break
        case 'strokeColor':
          this.strokeColor = options['strokeColor']
          this.spanElement.style.color = this.strokeColor
      }
    })
  }

  changeSize(scale) {
    // set the scale based off the current zoom (higher zoom => smaller label)
    scale = scale / Math.pow(2, DEFAULT_LABEL_ZOOM - this.map.getZoom())

    // set width, height, and fontSize for the visible elements
    this.containerElement.style.width = DEFAULT_LABEL_DIMENSIONS.baseWidth * scale + 'px'
    this.containerElement.style.height = DEFAULT_LABEL_DIMENSIONS.baseHeight * scale + 'px'
    this.spanElement.style.fontSize = DEFAULT_LABEL_DIMENSIONS.baseFontSize * scale + 'em'

    // resize rectangle and reposition
    let overlayProjection = this.getProjection()
    let latLngPxl = overlayProjection.fromLatLngToDivPixel(this.getCenter())

    this.containerElement.style.left = (latLngPxl.x - (this.containerElement.offsetWidth / 2)) + 'px'
    this.containerElement.style.top = (latLngPxl.y - (this.containerElement.offsetHeight / 2)) + 'px'

    let sw = overlayProjection.fromDivPixelToLatLng(new google.maps.Point(latLngPxl.x - (this.containerElement.offsetWidth / 2), latLngPxl.y + (this.containerElement.offsetHeight / 2)))
    let ne = overlayProjection.fromDivPixelToLatLng(new google.maps.Point(latLngPxl.x + (this.containerElement.offsetWidth / 2), latLngPxl.y - (this.containerElement.offsetHeight / 2)))
    let rectangleBounds = new google.maps.LatLngBounds(sw, ne)
    this.rectangle.setBounds(rectangleBounds)
  }

  /**
   * Called whn the overlay can attach itself to the map.
   * An overlay's receipt of onAdd() indicates that
   * the map's panes are available for attaching
   * the overlay to the map via the DOM.
   */

  onAdd() {
    this.containerElement = document.createElement('div')
    this.containerElement.style.border = 'none'
    this.containerElement.style.borderWidth = '0px'
    this.containerElement.style.position = 'absolute'
    this.containerElement.style.zIndex = '100001'
    if (this.dimensions) {
      this.containerElement.style.width = this.dimensions.baseWidth + 'px'
      this.containerElement.style.height = this.dimensions.baseHeight + 'px'
    }
    this.containerElement.style.cursor = 'move'
    this.containerElement.style.display = 'block'
    this.containerElement.style.backgroundColor = this.fillColor
    this.containerElement.style.textAlign = 'center'
    this.containerElement.style.overflow = 'hidden'
    this.containerElement.style.padding = '0 5px'
    this.spanElement = document.createElement('span')
    this.spanElement.textContent = this.text
    this.spanElement.style.display = 'inline-block'
    this.spanElement.style.color = this.strokeColor
    this.spanElement.style.verticalAlign = 'middle'
    this.spanElement.style.lineHeight = '1em'

    if (this.dimensions && this.dimensions.baseFontSize) {
      this.spanElement.style.fontSize = this.dimensions.baseFontSize + 'em'
    } else {
      this.spanElement.style.fontSize = '1em'
    }

    this.containerElement.appendChild(this.spanElement)

    google.maps.event.addDomListener(this.spanElement, 'keydown', e => {
      if (e.keyCode === 13) {
        e.preventDefault()
        this.spanElement.contentEditable = false
        this.spanElement.blur()
      }
    })

    google.maps.event.addDomListener(this.spanElement, 'keyup', e => {
      this.text = this.spanElement.textContent
      this.draw()
    })

    google.maps.event.addDomListener(this.spanElement, 'focusout', e => {
      google.maps.event.trigger(this, 'text_changed', this.spanElement.textContent)
      window.getSelection().removeAllRanges()
    })

    let panes = this.getPanes()
    panes.overlayLayer.appendChild(this.containerElement)

    let overlayProjection = this.getProjection()
    let latLngPxl = overlayProjection.fromLatLngToDivPixel(this.location)

    this.containerElement.style.left = (latLngPxl.x - (this.containerElement.offsetWidth / 2)) + 'px'
    this.containerElement.style.top = (latLngPxl.y - (this.containerElement.offsetHeight / 2)) + 'px'

    let sw = overlayProjection.fromDivPixelToLatLng(new google.maps.Point(latLngPxl.x - (this.containerElement.offsetWidth / 2), latLngPxl.y + (this.containerElement.offsetHeight / 2)))
    let ne = overlayProjection.fromDivPixelToLatLng(new google.maps.Point(latLngPxl.x + (this.containerElement.offsetWidth / 2), latLngPxl.y - (this.containerElement.offsetHeight / 2)))
    let rectangleBounds = new google.maps.LatLngBounds(sw, ne)
    this.initRectangle(rectangleBounds)

    this.enableEditMode && this.edit()
  }

  draw(skipFontSize) {
    let dimensions = this.getDimensions()

    this.containerElement.style.width = dimensions.width + 'px'
    this.containerElement.style.height = dimensions.height + 'px'
    this.containerElement.style.lineHeight = dimensions.height + 'px'

    if (!skipFontSize && (Math.abs(this.dimensions.baseWidth - dimensions.width) > 0.05)) {
      this.spanElement.style.fontSize = ((DEFAULT_LABEL_DIMENSIONS.baseFontSize * dimensions.width) / DEFAULT_LABEL_DIMENSIONS.baseWidth) + 'em'
    }

    const newDimensions = {
      baseWidth: this.containerElement.offsetWidth,
      baseHeight: this.containerElement.offsetHeight,
      baseFontSize: parseFloat(this.spanElement.style.fontSize)
    }

    const changedValues = Object.keys(newDimensions).map(k => {
      if (newDimensions[k] !== this.dimensions[k]) {
        this.dimensions[k] = newDimensions[k]
        return k
      }
    }).filter(Boolean)

    changedValues.length > 0 && google.maps.event.trigger(this, 'dimensions_changed', this.dimensions)

    let overlayProjection = this.getProjection()
    let latLngPxl = overlayProjection.fromLatLngToDivPixel(this.getCorners().nw)

    this.containerElement.style.left = latLngPxl.x + 'px'
    this.containerElement.style.top = latLngPxl.y + 'px'
  }

  /**
   * Removes this overlay from the map.
   */

  onRemove() {
    this.containerElement.parentNode.removeChild(this.containerElement)
    this.containerElement = null
    this.rectangle.setMap(null)
  }
}



// WEBPACK FOOTER //
// ./src/components/Markup/LabelOverlay.js